import { Alert, Group, Radio, Stack, Text, Tooltip } from "@mantine/core";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useInstallmentCalculation } from "../../../../../../../../hooks/useInstallmentCalculation";
import { AlertPresets } from "../../../../../../../../mantine/mantinePresets";
import urls from "../../../../../../../../urls";
import { AnchorLink } from "../../../../../../../AnchorLink/AnchorLink";
import { FormField } from "../../../../../../../BuildingBlocks/Forms/MantineFormFields/FormField";
import type { InstallmentConfiguration } from "../../../../../../DeliveryOverview.types";
import { InstallmentCalculationBase } from "../../../../../../DeliveryOverview.types";

interface InstallmentFormProps {
  contractId: string;
  isOpen: boolean;
  onChange: (installment: InstallmentConfiguration) => void;
  installment: InstallmentConfiguration;
  estimatedConsumptionBlocked?: boolean;
  historyInstallmentsBlocked?: boolean;
  advancePaymentsPlanAlreadyExists: boolean;
}

function InstallmentForm({
  onChange,
  installment,
  isOpen,
  contractId,
  estimatedConsumptionBlocked,
  historyInstallmentsBlocked,
  advancePaymentsPlanAlreadyExists
}: InstallmentFormProps) {
  const {
    data: installmentCalculation,
    isLoading,
    error
  } = useInstallmentCalculation(
    installment.year,
    contractId,
    isOpen &&
      installment.calculation_base ===
        InstallmentCalculationBase.HistoricalMeasurementValues
  );
  const [showWarning, setShowWarning] = useState(false);
  const { t } = useTranslation();

  function onChangeWithPossibleWarning(
    changedInstallment: InstallmentConfiguration
  ) {
    setShowWarning(advancePaymentsPlanAlreadyExists);
    onChange(changedInstallment);
  }

  function getFormFields() {
    if (
      installment.calculation_base ===
      InstallmentCalculationBase.EstimatedDeliveryAmount
    ) {
      return (
        <Group grow justify="space-between">
          <FormField
            decimalScale={3}
            key="installment-delivery-amount-in-kwh"
            label="Verbrauch im Jahr"
            min={0}
            placeholder="Verbrauch eintragen"
            rightSection="kWh"
            rightSectionWidth={40}
            type="number"
            value={installment.estimated_delivery_amount_in_kwh ?? undefined}
            onChange={(value) => {
              onChangeWithPossibleWarning({
                ...installment,
                estimated_delivery_amount_in_kwh: value
                  ? value.toString()
                  : null
              });
            }}
          />
          <FormField
            decimalScale={2}
            key="installment-estimated-share-direct-delivery-in-percent"
            label="Anteil Direktlieferung"
            max={100}
            min={0}
            placeholder="Anteil eintragen"
            rightSection="%"
            type="number"
            value={
              installment.estimated_share_direct_delivery_in_percent ??
              undefined
            }
            onChange={(value) => {
              onChangeWithPossibleWarning({
                ...installment,
                estimated_share_direct_delivery_in_percent: value
                  ? value.toString()
                  : null
              });
            }}
          />
        </Group>
      );
    } else if (
      installment.calculation_base ===
      InstallmentCalculationBase.EstimatedInstallmentPayment
    ) {
      return (
        <FormField
          decimalScale={2}
          key="installment-estimated-in-euro"
          label="Monatlicher Abschlag (Brutto)"
          min={0}
          placeholder="Abschlag eintragen"
          rightSection="€"
          type="number"
          value={installment.estimated_installment_in_euro ?? undefined}
          onChange={(value) => {
            onChangeWithPossibleWarning({
              ...installment,
              estimated_installment_in_euro: value ? value.toString() : null
            });
          }}
        />
      );
    } else if (
      installment.calculation_base ===
      InstallmentCalculationBase.HistoricalMeasurementValues
    ) {
      return (
        <>
          <Group grow justify="space-between">
            <FormField
              decimalScale={2}
              disabled
              label="Voraussichtlicher monatlicher Abschlag (brutto)"
              min={0}
              placeholder={
                isLoading
                  ? "Abschlag wird berechnet..."
                  : error
                    ? "Kann nicht ermittelt werden"
                    : ""
              }
              rightSection="€"
              type="number"
              value={installmentCalculation}
              onChange={() => {}}
            />
            <FormField
              decimalScale={2}
              disabled={isLoading || !!error}
              key="user-requested-installment-in-euro"
              label="Gewünschten Abschlag korrigieren (Brutto)"
              min={0}
              placeholder="Abschlag eintragen"
              rightSection="€"
              type="number"
              value={
                installment.user_requested_installment_in_euro ?? undefined
              }
              onChange={(value) => {
                onChangeWithPossibleWarning({
                  ...installment,
                  user_requested_installment_in_euro: value
                    ? value.toString()
                    : null
                });
              }}
            />
          </Group>
          {error && error.response && (
            <Alert
              color={AlertPresets.error.color}
              icon={AlertPresets.error.icon}
            >
              <Trans
                components={{
                  support: (
                    <AnchorLink href={urls.optiSupport("mieterstrom")}>
                      Support
                    </AnchorLink>
                  )
                }}
                i18nKey={
                  error.response.status === 400
                    ? `errors.ContractForm.InstallmentCalculation.${error.response.data[0]}`
                    : "errors.ServerError"
                }
                t={t}
                values={{
                  year: installment.year,
                  prevYear: installment.year - 1
                }}
              />
            </Alert>
          )}
        </>
      );
    }
  }

  return (
    <Stack className="InstallmentForm" key={installment.year}>
      <Radio.Group
        label="Der jährliche Betrag für den Abschlagsplan basiert auf:"
        value={installment.calculation_base}
        onChange={(value) =>
          onChangeWithPossibleWarning({
            ...installment,
            calculation_base: value as InstallmentCalculationBase
          })
        }
      >
        <Group>
          <Radio
            disabled={historyInstallmentsBlocked}
            key={InstallmentCalculationBase.HistoricalMeasurementValues}
            label="Historischen Werten"
            value={InstallmentCalculationBase.HistoricalMeasurementValues}
          />
          <Radio
            key={InstallmentCalculationBase.EstimatedInstallmentPayment}
            label="Geschätztem Abschlag"
            value={InstallmentCalculationBase.EstimatedInstallmentPayment}
          />
          <Tooltip
            disabled={!estimatedConsumptionBlocked}
            label={t(
              "messages.MieterstromContract.EstimatedConsumptionBlocked"
            )}
          >
            <Radio
              disabled={estimatedConsumptionBlocked}
              key={InstallmentCalculationBase.EstimatedDeliveryAmount}
              label="Geschätztem Verbrauch"
              value={InstallmentCalculationBase.EstimatedDeliveryAmount}
            />
          </Tooltip>
        </Group>
      </Radio.Group>
      {getFormFields()}
      {showWarning && (
        <Alert
          color={AlertPresets.warning.color}
          icon={AlertPresets.warning.icon}
          withCloseButton
          onClose={() => setShowWarning(false)}
        >
          Es existiert bereits ein gültiger Abschlagsplan. Archivieren Sie den
          bestehenden Abschlagsplan und erstellen Sie dann einen neuen, damit
          die Änderungen wirksam werden.
        </Alert>
      )}
      <Text mt="lg">
        Mein Kunde hat bereits folgende Abschlagszahlungen geleistet:
      </Text>
      <FormField
        decimalScale={2}
        key="installment-payments-made"
        label="Geleistete Zahlungen im Jahr"
        min={0}
        placeholder="Zahlungen eintragen"
        rightSection="€"
        type="number"
        value={installment.installment_payments_made ?? undefined}
        onChange={(value) => {
          onChange({
            ...installment,
            installment_payments_made: value ? value.toString() : null
          });
        }}
      />
    </Stack>
  );
}

export { InstallmentForm };
